import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  ASSET_TYPES,
  ASSET_TYPE_LABEL,
  ASSET_VISIBILITIES,
  ASSET_VISIBILITY_LABEL,
  EMBED_PROVIDERS,
  EMBED_PROVIDER_LABEL,
  PARTNER_TYPES,
  PARTNER_TYPE_LABEL,
  type AssetType,
  type AssetVisibility,
  type EmbedProvider,
  type PartnerType,
} from "@colab/shared";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { formatApiError } from "@/lib/format-api-error";
import type { ContentAssetRow } from "./types";
import { useUpdateAsset } from "./use-update-asset";

// Phase 7 sub-bucket 13. SPA port of legacy EditAssetDialog. Pure metadata
// editor — file replacement lives on the detail page (the future Replace
// button re-runs the three-step upload). Fields are local state seeded
// from the asset; a single Save fires one PATCH with the diff.
export function EditAssetDialog({
  asset,
  open,
  onOpenChange,
}: {
  asset: ContentAssetRow;
  open: boolean;
  onOpenChange: (next: boolean) => void;
}) {
  const update = useUpdateAsset();
  const isEmbed = !!asset.embed_provider;
  const isExternal = !!asset.external_url && !asset.embed_provider;

  const [title, setTitle] = useState(asset.title);
  const [description, setDescription] = useState(asset.description ?? "");
  const [assetType, setAssetType] = useState<AssetType>(asset.asset_type);
  const [visibility, setVisibility] = useState<AssetVisibility>(asset.visibility);
  const [partnerTypes, setPartnerTypes] = useState<Set<PartnerType>>(
    new Set(asset.partner_type_restriction),
  );
  const [categoriesRaw, setCategoriesRaw] = useState(asset.categories.join(", "));
  const [externalUrl, setExternalUrl] = useState(asset.external_url ?? "");
  const [embedProvider, setEmbedProvider] = useState<EmbedProvider>(
    asset.embed_provider ?? "loom",
  );
  const [embedUrl, setEmbedUrl] = useState(asset.embed_url ?? "");

  function togglePartnerType(t: PartnerType) {
    setPartnerTypes((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      return next;
    });
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) {
      toast.error("Title is required");
      return;
    }
    const categories = categoriesRaw
      .split(",")
      .map((c) => c.trim())
      .filter((c) => c.length > 0);
    try {
      await update.mutateAsync({
        id: asset.id,
        patch: {
          title: title.trim(),
          ...(asset.is_folder ? {} : { description: description.trim() || null }),
          ...(asset.is_folder ? {} : { asset_type: assetType }),
          visibility,
          partner_type_restriction: Array.from(partnerTypes),
          ...(asset.is_folder ? {} : { categories }),
          ...(isEmbed
            ? { embed_provider: embedProvider, embed_url: embedUrl.trim() || null }
            : {}),
          ...(isExternal ? { external_url: externalUrl.trim() || null } : {}),
        },
      });
      toast.success("Saved");
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err, "Failed to save"));
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (update.isPending) return;
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">
            Edit {asset.is_folder ? "folder" : "asset"}
          </DialogTitle>
          <DialogDescription>
            Metadata only. To replace the underlying file, use the Replace button
            on the asset detail page.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              autoFocus
            />
          </div>

          {!asset.is_folder && (
            <div className="space-y-1.5">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            {!asset.is_folder && (
              <div className="space-y-1.5">
                <Label htmlFor="asset_type">Type</Label>
                <select
                  id="asset_type"
                  value={assetType}
                  onChange={(e) => setAssetType(e.target.value as AssetType)}
                  className="h-9 w-full rounded-md border bg-background px-2.5 text-sm"
                >
                  {ASSET_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {ASSET_TYPE_LABEL[t]}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="visibility">Visibility</Label>
              <select
                id="visibility"
                value={visibility}
                onChange={(e) => setVisibility(e.target.value as AssetVisibility)}
                className="h-9 w-full rounded-md border bg-background px-2.5 text-sm"
              >
                {ASSET_VISIBILITIES.map((v) => (
                  <option key={v} value={v}>
                    {ASSET_VISIBILITY_LABEL[v]}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <Label className="text-xs">Restrict to partner types</Label>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {PARTNER_TYPES.map((t) => (
                <label
                  key={t}
                  className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border bg-background px-2 py-1 text-xs hover:bg-muted has-[:checked]:border-accent has-[:checked]:bg-accent/10 has-[:checked]:text-accent"
                >
                  <input
                    type="checkbox"
                    checked={partnerTypes.has(t)}
                    onChange={() => togglePartnerType(t)}
                    className="h-3 w-3 accent-accent"
                  />
                  {PARTNER_TYPE_LABEL[t]}
                </label>
              ))}
            </div>
          </div>

          {!asset.is_folder && (
            <div className="space-y-1.5">
              <Label htmlFor="categories">Categories (comma-separated)</Label>
              <Input
                id="categories"
                value={categoriesRaw}
                onChange={(e) => setCategoriesRaw(e.target.value)}
                placeholder="meeting link, slides, banner"
              />
            </div>
          )}

          {isEmbed && (
            <div className="space-y-3 border-t pt-4">
              <div className="space-y-1.5">
                <Label htmlFor="embed_provider">Provider</Label>
                <select
                  id="embed_provider"
                  value={embedProvider}
                  onChange={(e) => setEmbedProvider(e.target.value as EmbedProvider)}
                  className="h-9 w-full rounded-md border bg-background px-2.5 text-sm"
                >
                  {EMBED_PROVIDERS.map((p) => (
                    <option key={p} value={p}>
                      {EMBED_PROVIDER_LABEL[p]}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="embed_url">Embed URL</Label>
                <Input
                  id="embed_url"
                  type="url"
                  value={embedUrl}
                  onChange={(e) => setEmbedUrl(e.target.value)}
                />
              </div>
            </div>
          )}

          {isExternal && (
            <div className="space-y-1.5 border-t pt-4">
              <Label htmlFor="external_url">External URL</Label>
              <Input
                id="external_url"
                type="url"
                value={externalUrl}
                onChange={(e) => setExternalUrl(e.target.value)}
              />
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={update.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={update.isPending}>
              {update.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
